import React from "react";
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { selectChartEventsSettings } from "../../selectors/chart";
import { updateChartConfig } from "../../reducer/chart.reducer";
import { EventsSettings as EventsSettingsType } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import { defaultEventsSettings } from "../../util/eventsSettingsDefaults";
import { chartSettingsFormGroupSx, chartSettingsColorRowSx, chartSettingsSectionTitleSx, chartSettingsCheckboxColorLabelSx, chartSettingsCheckboxLabelSx } from "./chartSettingsLayout";

const checkboxLabelSx = chartSettingsCheckboxLabelSx;
const checkboxColorLabelSx = chartSettingsCheckboxColorLabelSx;

type Props = {
    viewKey: string;
};

const EventsSettings = ({ viewKey }: Props) => {
    const dispatch = useDispatch();
    const eventsSettings = useSelector(selectChartEventsSettings(viewKey));

    const showDividends = eventsSettings?.showDividends ?? defaultEventsSettings.showDividends;
    const showSplits = eventsSettings?.showSplits ?? defaultEventsSettings.showSplits;
    const showEarnings = eventsSettings?.showEarnings ?? defaultEventsSettings.showEarnings;
    const showSessionBreaks = eventsSettings?.showSessionBreaks ?? defaultEventsSettings.showSessionBreaks;
    const showLatestNews = eventsSettings?.showLatestNews ?? defaultEventsSettings.showLatestNews;
    const sessionBreakColor = eventsSettings?.sessionBreakColor ?? defaultEventsSettings.sessionBreakColor;
    const sessionBreakColorOpacity = eventsSettings?.sessionBreakColorOpacity ?? defaultEventsSettings.sessionBreakColorOpacity;
    const sessionBreakLineStyle = eventsSettings?.sessionBreakLineStyle ?? defaultEventsSettings.sessionBreakLineStyle;

    const updateSettings = (partial: Partial<EventsSettingsType>) => {
        dispatch(updateChartConfig({
            viewKey,
            config: {
                eventsSettings: {
                    ...eventsSettings,
                    ...partial,
                },
            },
            createIfAbsent: false,
        }));
    };

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        field: keyof EventsSettingsType
    ) => {
        updateSettings({ [field]: event.target.checked });
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                EVENTS
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!showDividends}
                            onChange={(e) => handleCheckboxChange(e, "showDividends")}
                        />
                    }
                    label="Dividends"
                    sx={checkboxLabelSx}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!showSplits}
                            onChange={(e) => handleCheckboxChange(e, "showSplits")}
                        />
                    }
                    label="Splits"
                    sx={checkboxLabelSx}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!showEarnings}
                            onChange={(e) => handleCheckboxChange(e, "showEarnings")}
                        />
                    }
                    label="Earnings"
                    sx={checkboxLabelSx}
                />
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!showSessionBreaks}
                                onChange={(e) => handleCheckboxChange(e, "showSessionBreaks")}
                            />
                        }
                        label="Session breaks"
                        sx={checkboxColorLabelSx}
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={sessionBreakColor}
                        opacity={sessionBreakColorOpacity}
                        lineStyle={sessionBreakLineStyle}
                        onColorChange={(color, opacity) =>
                            updateSettings({ sessionBreakColor: color, sessionBreakColorOpacity: opacity })
                        }
                        onLineStyleChange={(style) =>
                            updateSettings({ sessionBreakLineStyle: style })
                        }
                    />
                </Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!showLatestNews}
                            onChange={(e) => handleCheckboxChange(e, "showLatestNews")}
                        />
                    }
                    label="News"
                    sx={checkboxLabelSx}
                />
            </FormGroup>
        </Box>
    );
};

export default EventsSettings;
